import { useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import useModalState from '@sdk/components/ui/modal/useModalState';
import Modal from '@sdk/components/ui/modal/Modal';
import gettext from '@sdk/lang/gettext';
import ModalBodyWithHeader from '@sdk/components/ui/modal/ModalBodyWithHeader';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Row from '@sdk/components/ui/spacing/Row';
import ModalFooterActionButtons from '@sdk/components/ui/modal/ModalFooterActionButtons';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import ToastComponent from '@sdk/common/toast/ToastComponent';
import type { LocationType } from '@sdk/locations/types/LocationType';

import BodyPortal from '@web/components/BodyPortal';
import LocationSvg from '@web/assets/svg/Icons/32/location-blue.svg';

import { TextButton } from './importLocations.styles';
import { useImportLocations } from './importLocations.hooks';
import UploadFile from './UploadFile/UploadFile';
import MapColumns from './MapColumns/MapColumns';

type Props = {
  onLocationsImported: (locations: LocationType[]) => void;
};

const ImportLocations = ({ onLocationsImported }: Props) => {
  const { isVisible, open, close } = useModalState();
  const config = useRef({ noDismiss: true }).current;
  const dropzoneState = useDropzone({ maxFiles: 1 });
  const {
    uploadError,
    parsedFileData,
    setParsedFileData,
    updateExistingLocations,
    setUpdateExistingLocations,
    defaultCountryCode,
    setDefaultCountryCode,
    pickedColumnsDict,
    columnMap,
    setColumnMap,
    rowSelection,
    setRowSelection,
    selectedRowsIndices,
    submit,
    errorText,
  } = useImportLocations(dropzoneState.acceptedFiles, onLocationsImported, close);

  const openModal = () => {
    setParsedFileData(null);
    open();
  };

  return (
    <>
      <TextButton onClick={openModal}>{gettext('Import locations')}</TextButton>
      <BodyPortal>
        <Modal fixed config={config} isVisible={isVisible} onDismiss={close} closePositionTop={24}>
          <ModalBodyWithHeader
            width={770}
            titleThemeColor={ThemeColorType.primary}
            titleText={gettext('Import locations')}
            close={close}
            titleIcon={<LocationSvg />}>
            {parsedFileData ? (
              <MapColumns
                updateExistingLocations={updateExistingLocations}
                setUpdateExistingLocations={setUpdateExistingLocations}
                defaultCountryCode={defaultCountryCode}
                setDefaultCountryCode={setDefaultCountryCode}
                parsedFileData={parsedFileData}
                columnMap={columnMap}
                setColumnMap={setColumnMap}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                pickedColumnsDict={pickedColumnsDict}
              />
            ) : (
              <UploadFile uploadError={uploadError} dropzoneState={dropzoneState} />
            )}
            <Row mTop={20}>
              <HorizontalSeparator />
            </Row>
            <ModalFooterActionButtons
              mTop={20}
              cancel={close}
              cancelText={gettext('Cancel')}
              submitText={gettext('Import')}
              allowSubmitCallbackIfInactive
              submit={submit}
              isSubmitActive={Boolean(pickedColumnsDict.name && selectedRowsIndices.length)}
              isSubmitVisible={Boolean(parsedFileData)}
              errorText={errorText}
            />
          </ModalBodyWithHeader>
        </Modal>
      </BodyPortal>
      <ToastComponent />
    </>
  );
};

export default ImportLocations;
